import React, { useEffect, useState } from 'react';

import './reservationInsightsPage.scss';

import ReservationInsightsFilter from '../interfaces/reservationInsightsFilter';
import ReportFilter from './reportfilter/reportFilter';
import LanguageProvider from '@/providers/languageProvider';
import ReservedTimeGauge from './charts/reservedTimeGauge';
import ReservedTimeGrid from './charts/reservedTimeGrid';
import HistoricReservationService from './historicReservationService';
import { Floor, Venue, Room, Workspace, FilterOptions } from '../interfaces/filterOptions';
import VenueProvider from '@/providers/venueProvider';
import ReservedLocationChart from './charts/reservedLocationChart';
import ReservedHours from './charts/reservedHours';
import ReservedPlaceTypeChart from './charts/reservedPlaceTypeChart';
import ReservedPeriodChart from './charts/reservedPeriodChart';
import FullPageLoader from '@/components/loaders/fullPageLoader';
import Teaser from "@/components/teaser/realEstateUtilizationTeaser";
import SubscriptionValidationService from '@/services/subscriptionValidationService';
import AppEventHub, { AppEvents } from "utils/appEventHub";
import FilterUtils from './reportfilter/filter.utils';

const ReservationInsightsPage: React.FC<{}> =  (props) => {
    const [hasSubscription, setHasSubscription] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [venues, setVenues] = useState<Venue[]>([]);
    const [floors, setFloors] = useState<Floor[]>([]);
    const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
    const [rooms, setRooms] = useState<Room[]>([]);
    const [filter, setFilter] = useState<ReservationInsightsFilter>();
    const [filterOptionsChanged, setFilterOptionsChanged] = useState<number>(Date.now);
    const [filterOptions, setFilterOptions] = useState<FilterOptions>();

    const historicReservationService = new HistoricReservationService();

    useEffect(() => {
        setLoading(true);
        historicReservationService.getPlaceFilterOptions().then((filterOptions) => {

            FilterUtils.enrichFilterOptions({...filterOptions});

            setVenues(filterOptions.venues);
            setFloors(filterOptions.floors);
            setWorkspaces(filterOptions.workspaces);
            setRooms(filterOptions.rooms);
            setFilterOptionsChanged(Date.now);
            setLoading(false);
            setFilterOptions(filterOptions);
        });

        initializeActiveVenueAsync();

        AppEventHub.on(AppEvents.BuildingSelected, initializeActiveVenueAsync);

        return (): void => {
            AppEventHub.off(AppEvents.BuildingSelected, initializeActiveVenueAsync);
        };

    }, []);

    async function initializeActiveVenueAsync(): Promise<void> {
        const activeVenue = VenueProvider.getActiveVenue();
        const subscriptionValidationService = await SubscriptionValidationService.GetInstanceAsync();
        const hasReservationInsights = await subscriptionValidationService.venueHasAnyApplicableSubscription(activeVenue!, ["BEFMISRooms", "BEFMISWorkspaces"]);

        setHasSubscription(hasReservationInsights);
    }

    function handleUpdateFilter(filterParam: ReservationInsightsFilter): void {
        setFilter({ ...filterParam, triggerUpdate: Date.now() });
    }

    return (<>
        {hasSubscription && !loading && 
            <div>
                <ReportFilter
                    id="reportFilter"
                    loading={loading}
                    venues={venues}
                    floors={floors}
                    workspaces={workspaces}
                    rooms={rooms}
                    triggerUpdate={filter?.triggerUpdate || 0}
                    filterOptionsChanged={filterOptionsChanged}
                    onUpdate={handleUpdateFilter} />
            </div>}
        {hasSubscription && (!loading && filter && venues && venues.length > 0 && filterOptions) && <div className="report-content">
            <div className="widget widget-reserved-total" >
                <ReservedTimeGauge
                    id="reservedTime"
                    filter={filter}
                    title={LanguageProvider.getTranslation("pages.realestateutilization.charts.reservedtimechart.reservedtime")}
                />
            </div>
            <div className="widget widget-reserved-hours">
                <ReservedHours filter={filter}></ReservedHours>
            </div>
            <div className="widget widget-reserved-time">
                <ReservedTimeGrid filter={filter}></ReservedTimeGrid>
            </div>
            <div className="widget widget-reserved-moment">
                <ReservedPeriodChart filter={filter} id="reservedmoment-percentage"></ReservedPeriodChart>
            </div>
            <div className="widget widget-reserved-location">
                <ReservedLocationChart filter={filter} filterOptions={filterOptions} id="reservedlocation-percentage"></ReservedLocationChart>
            </div>
            <div className="widget widget-reserved-placetype">
                <ReservedPlaceTypeChart filter={filter} id="reservedplacetype-percentage"></ReservedPlaceTypeChart>
            </div>
        </div>}
        {(!filter || !venues || venues.length === 0) && <div className="report-loader"><FullPageLoader loading={true} viewHeightPxReduction={180} /></div>}
        {!hasSubscription && <Teaser></Teaser>}
    </>);
}

export default ReservationInsightsPage;